<script lang="ts" setup>
import { clickThemeInput } from "../preinstall/preinstall";
import { step, isDocx, isPerinstall } from "../use/data";
import { sendMsg } from "../use/methods";
import { ref, Ref } from "vue";
import PreinstallTemplate from "../components/preinstallTemplate.vue";
import { openLoginDialog } from "@/components/content/loginDialog/login";
import useStore from "@/stores/publicStore";
import { storeToRefs } from "pinia";

const store = useStore();
const { isLogin } = storeToRefs(store);
const route = useRoute();
const router = useRouter();

const themeValue: Ref<string> = ref("");

const overwriteNativeAjax = () => {
  //返回空的promise，阻止上传
  return new Promise(() => {});
};

const generationWayList = [
  { title: "输入主题生成PPT", type: "input-theme" },
  { title: "导入文档生成PPT", type: "import-docx" },
];
const generationWay: Ref<string> = ref("input-theme");

const beforeUpload = (rawFile: any) => {
  if (!isLogin.value) {
    ElMessage.warning("请先登录");
    openLoginDialog();
    return false;
  }
  //上传文件格式只能是docx
  if (
    rawFile.type !==
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    ElMessage.error("请确认上传文件格式为docx");
    return false;
  } else if (rawFile.size / 1024 / 1024 > 5) {
    ElMessage.error("请确认上传文件大小不能超过5M");
    return false;
  }

  const reader = new FileReader();
  reader.onload = async () => {
    const mammoth = await import("mammoth");
    const arrayBuffer = reader.result;
    // 解析.docx文件
    if (arrayBuffer !== null && typeof arrayBuffer !== "string") {
      mammoth
        .extractRawText({ arrayBuffer })
        .then((result) => {
          themeValue.value = result.value; // 提取到的纯文本内容
          isDocx.value = 1;
          sendMsg(themeValue.value, 1);
          isPerinstall.value = false;
          step.value = "generation";
        })
        .catch(() => {
          ElMessage.error(
            "请确认文件是docx格式，且不是直接修改doc后缀名的文件"
          );
        });
    }
  };
  reader.onerror = function () {
    //报错
    ElMessage.error("文件读取失败");
  };
  reader.readAsArrayBuffer(rawFile);
};

const sendTheme = () => {
  if (!themeValue.value) {
    ElMessage.error("请输入主题");
    return;
  }
  if (!isLogin.value) {
    ElMessage.warning("请先登录");
    openLoginDialog();
    return;
  }
  isPerinstall.value = false;
  step.value = "generation";
  sendMsg(themeValue.value, 0);
};

// watchEffect(() => {

// });

watchEffect(() => {
  if (route.query) {
    const n = route.query;
    if (n.theme) {
      generationWay.value = "input-theme";
      themeValue.value = n.theme as string;
      setTimeout(() => {
        sendTheme();
      }, 1000);
    }
    if (n.type == "upload") {
      generationWay.value = "import-docx";
    }
  }
});

onUnmounted(() => {
  // 删除路径的
  router.replace({ query: {} });
});
</script>

<template>
  <div class="slide-ppt-theme">
    <div class="slide-ppt-theme-main">
      <div class="slide-ppt-theme-main-content full flex-center scroll">
        <div mx-auto flex-align flex-col h-650>
          <div text-40 font-700 text="#111827">AI一键生成PPT</div>
          <div mt-10 text-center text-16 mb-20 text="#4B5563">
            仅需10秒，AI自动生成PPT
          </div>

          <div
            w-310
            h-42
            rounded-full
            bg="#ffffff96"
            p-3
            flex-align
            justify-between
          >
            <div
              v-for="item in generationWayList"
              :key="item.type"
              w-150
              h-full
              flex-center
              rounded-full
              cursor-pointer
              :class="{ 'text-white bg-#4C83FC': generationWay === item.type }"
              @click="generationWay = item.type"
            >
              {{ item.title }}
            </div>
          </div>

          <div v-show="generationWay == 'input-theme'">
            <div class="input-background my-15 mx-auto relative">
              <PreinstallTemplate />
              <div class="bg-white rounded-8 flex-align px-12">
                <el-input
                  v-model="themeValue"
                  type="text"
                  :clearable="false"
                  :placeholder="`输入您想要的PPT主题，就可以开始智能生成啦~`"
                  @keyup.enter="sendTheme"
                  @click.stop="clickThemeInput"
                />
                <div
                  class="send-theme"
                  :class="{ 'gray-scale': themeValue == '' }"
                  @click="sendTheme"
                >
                  <text class="iconfont ppt-send"></text>
                </div>
              </div>
            </div>
            <CsdImg
              :src="`/images/show-pptist.gif`"
              class="mt-30 shadow-xl rounded-8"
            />
          </div>
          <div v-show="generationWay == 'import-docx'">
            <div
              class="import-background bg-#ffffff w-540 px-17 pt-28 pb-22 mt-14 rounded-10"
            >
              <div class="text-center color-#333 mb-18">
                AI将根据导入的文档，自动生成PPT
              </div>
              <el-upload
                action="#"
                accept=".docx"
                :show-file-list="false"
                :before-upload="beforeUpload"
                :http-request="overwriteNativeAjax"
              >
                <div
                  class="h-266 w-506 rounded-4 border border-#4096FF border-dashed flex-center flex-col"
                >
                  <CsdImg src="/images/aiDoc/upload-doc-icon.svg" />
                  <div class="text-center my-8 text-#333333">上传文档</div>
                  <div class="color-#999999 text-12 text-center leading-16">
                    仅支持docx文件格式丨文档限制最大5M；
                    <br />*建议选择4500字以内的 word 文档，超过长度将自动截断
                  </div>
                </div>
              </el-upload>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.slide-ppt-theme {
  width: 100%;
  height: 100%;
  padding: 15px;
  // background-color: white;
  position: relative;

  &-main {
    z-index: 2;
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;

    &-content {
      padding: 50px 0;
      margin: 0 auto;

      .input-background {
        width: 540px;
        // background-color: #4c83fc;
        background: linear-gradient(90deg, #4c83fc 0%, #5ddae8 100%);
        padding: 4px;
        border-radius: 12px;

        .el-input {
          height: 46px !important;
          flex: 1;
          font-size: 14px;

          .el-input__wrapper {
            padding: 0;
          }
        }

        .send-theme {
          border-radius: 100%;
          padding: 8px;
          background: linear-gradient(180deg, #13dbe6 0%, #518bf6 100%);
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;

          &:hover {
            background: linear-gradient(180deg, #13dbe699 0%, #518bf699 100%);
          }

          &:active {
            background: linear-gradient(180deg, #13dbe6 0%, #518bf6 100%);
          }

          .iconfont {
            color: white;
            transform: translateX(-1px);
          }
        }

        .gray-scale {
          background: #d0d2d4 !important;
        }
      }

      .import-background {
        box-shadow: 0px 3px 10px 1px rgba(0, 0, 0, 0.05);
      }
    }
  }
}
</style>
